/**
 * m3cull web schema (v1).
 *
 * Carried over from the Electron build with the path columns replaced: a file is
 * identified by (source_id, rel_path) and resolved through the source's stored
 * directory handle. Nothing in this database is machine-specific.
 */

export const SCHEMA_VERSION = 3;

export const SCHEMA_SQL = `
CREATE TABLE IF NOT EXISTS schema_version (
  version INTEGER PRIMARY KEY
);

CREATE TABLE IF NOT EXISTS settings (
  key   TEXT PRIMARY KEY,
  value TEXT NOT NULL
);

CREATE TABLE IF NOT EXISTS projects (
  id                   INTEGER PRIMARY KEY AUTOINCREMENT,
  name                 TEXT    NOT NULL,
  auto_advance         INTEGER NOT NULL DEFAULT 1,
  preview_target_px    INTEGER NOT NULL DEFAULT 2880,
  created_at           INTEGER NOT NULL,
  updated_at           INTEGER NOT NULL,
  last_opened_at       INTEGER,
  last_viewed_media_id INTEGER
);

CREATE TABLE IF NOT EXISTS sources (
  id         INTEGER PRIMARY KEY AUTOINCREMENT,
  project_id INTEGER NOT NULL,
  name       TEXT    NOT NULL,
  added_at   INTEGER NOT NULL,
  FOREIGN KEY (project_id) REFERENCES projects(id) ON DELETE CASCADE
);

CREATE TABLE IF NOT EXISTS buckets (
  id            INTEGER PRIMARY KEY AUTOINCREMENT,
  project_id    INTEGER NOT NULL,
  name          TEXT    NOT NULL,
  kind          TEXT    NOT NULL CHECK (kind IN ('keep', 'reject')),
  hotkey        TEXT    NOT NULL,
  display_order INTEGER NOT NULL,
  FOREIGN KEY (project_id) REFERENCES projects(id) ON DELETE CASCADE
);

CREATE TABLE IF NOT EXISTS media (
  id                INTEGER PRIMARY KEY AUTOINCREMENT,
  project_id        INTEGER NOT NULL,
  source_id         INTEGER,
  rel_path          TEXT    NOT NULL,
  filename          TEXT    NOT NULL,
  type              TEXT    NOT NULL CHECK (type IN ('image', 'video')),
  ext               TEXT    NOT NULL,
  size_bytes        INTEGER,
  width             INTEGER,
  height            INTEGER,
  duration_ms       INTEGER,
  thumb_key         TEXT,
  preview_key       TEXT,
  status            TEXT    NOT NULL DEFAULT 'ok'
                      CHECK (status IN ('ok', 'missing', 'error')),
  status_checked_at INTEGER,
  bucket_id         INTEGER,
  rotation          INTEGER NOT NULL DEFAULT 0,
  created_at        INTEGER NOT NULL,
  modified_at       INTEGER,
  FOREIGN KEY (project_id) REFERENCES projects(id) ON DELETE CASCADE,
  FOREIGN KEY (source_id)  REFERENCES sources(id)  ON DELETE CASCADE,
  FOREIGN KEY (bucket_id)  REFERENCES buckets(id)  ON DELETE SET NULL,
  UNIQUE (project_id, source_id, rel_path)
);

-- Review Assist (schema v2). Purely additive: an existing database gains the
-- table on the next boot and not a single existing row is touched.
--
-- One row per analysed photograph. The embedding is kept so that changing a
-- threshold, or adding frames to a shoot, never re-runs the model.
CREATE TABLE IF NOT EXISTS media_assist (
  media_id   INTEGER PRIMARY KEY,
  sharpness  REAL    NOT NULL,
  is_soft    INTEGER NOT NULL DEFAULT 0,
  burst_id   INTEGER,
  is_pick    INTEGER NOT NULL DEFAULT 0,
  embedding  BLOB,
  model      TEXT,
  scored_at  INTEGER NOT NULL,
  FOREIGN KEY (media_id) REFERENCES media(id) ON DELETE CASCADE
);

CREATE INDEX IF NOT EXISTS idx_assist_burst ON media_assist(burst_id);

-- Focus, measured at a size where it can actually be seen.
--
-- Its own table rather than a column on media_assist, for two reasons. It has a
-- different lifecycle: media_assist.sharpness is screened from the 240px
-- thumbnail for free on every shoot, while this is measured from the original
-- during a review the photographer opted into, and is re-measured whenever the
-- measure itself changes. And there is no migration runner in this build --
-- sqlite-client just execs this file -- so an added column would silently never
-- appear on a database that already exists, while CREATE TABLE IF NOT EXISTS
-- reaches every one of them without putting the expensive embeddings at risk.
CREATE TABLE IF NOT EXISTS media_focus (
  media_id    INTEGER PRIMARY KEY,
  sharpness   REAL    NOT NULL,
  tag         TEXT    NOT NULL,
  measured_at INTEGER NOT NULL,
  FOREIGN KEY (media_id) REFERENCES media(id) ON DELETE CASCADE
);

CREATE INDEX IF NOT EXISTS idx_media_project ON media(project_id);
CREATE INDEX IF NOT EXISTS idx_media_bucket  ON media(bucket_id);
CREATE INDEX IF NOT EXISTS idx_media_source  ON media(source_id);
CREATE INDEX IF NOT EXISTS idx_media_order   ON media(project_id, rel_path, id);

INSERT OR IGNORE INTO schema_version (version) VALUES (${SCHEMA_VERSION});
`;

/** Canonical ordering. Every query that pages or seeks must use exactly this. */
export const MEDIA_ORDER = 'rel_path COLLATE NOCASE ASC, id ASC';
